import React, { lazy, ReactNode, Suspense, useState } from 'react';

import Button from '../../../button/button';
import Tabs, { Tab } from '../../../tabs/tabs';
import Typography from '../../../typography/typography';
import { EVENTS } from '../../fitment-constants';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import { isArrayWithLength } from '../../../../shared/utils/validators/validators';

import { Fitment } from '../../../../shared/types/fitment';
import { FITMENT_TYPES } from '../../../../shared/constants/fitment';

import './fitment-recent-vehicles.redesign.scss';

const FitmentList = lazy(
  () =>
    import(
      /* webpackChunkName: "fitment-list" */
      '../fitment-list/fitment-list.redesign'
    )
);

const SAVED_TAB = {
  description: 'Saved Vehicles',
  id: FITMENT_TYPES.SAVED
};

const RECENT_TAB = {
  description: 'Recent Searches',
  id: FITMENT_TYPES.RECENT
};

const TABS = [SAVED_TAB, RECENT_TAB];

interface FitmentTab {
  description: string;
  id: string;
}

interface FitmentRecentVehiclesProps {
  addVehicleOnClick: () => void;
  canToggleVehicle: boolean;
  hideDelete?: boolean;
  hideEditLink?: boolean;
  isLoggedIn: boolean;
  onEditFitment: (vehicleCrumbToEdit?: string) => void;
  onEvent: ({ name, payload }) => void;
  onFitmentSelected: () => void;
  onSelectFitment: () => void;
  recentFitments: Fitment[];
  renderAddNewVehicleButton?: () => ReactNode;
  renderSavedFitmentsMessage?: (isLoggedIn?: boolean) => ReactNode;
  savedFitments: Fitment[];
  selectedFitment: Fitment;
}

function FitmentRecentVehicles(props: FitmentRecentVehiclesProps) {
  const {
    addVehicleOnClick,
    canToggleVehicle,
    hideDelete,
    hideEditLink,
    isLoggedIn,
    onEditFitment,
    onEvent,
    onFitmentSelected,
    onSelectFitment,
    recentFitments,
    renderAddNewVehicleButton,
    renderSavedFitmentsMessage,
    savedFitments,
    selectedFitment
  } = props;
  const isSelectedFitmentSaved =
    isArrayWithLength(savedFitments) &&
    savedFitments.some(fitment => fitment.uid === selectedFitment?.uid);
  const [selectedTab, setSelectedTab] = useState<FitmentTab>(
    isLoggedIn && (!selectedFitment || isSelectedFitmentSaved)
      ? TABS[0]
      : TABS[1]
  );

  const handleEditFitment = fitment => {
    trackAction('vehicle_modal_edit');
    onEvent({
      name: EVENTS.EDIT_SELECTED_FITMENT,
      payload: fitment
    });
    onEditFitment();
  };

  const onTabSelected = selectedTab => {
    onEvent({
      name: EVENTS.VEHICLE_TAB_SELECTED,
      payload: selectedTab
    });
    setSelectedTab(selectedTab);
  };

  return (
    <>
      <Typography className="fitment-title" variant="H3">
        Your Vehicles
      </Typography>
      <Tabs isSecondary styleName="tabs-container">
        {TABS.map(tab => (
          <Tab
            isSelected={selectedTab.id === tab.id}
            key={tab.id}
            onClick={() => onTabSelected(tab)}
            styleName="tab"
          >
            {tab.description.split(' ').map((word, idx) => (
              <Typography key={word} weight={idx === 0 ? 'medium' : 'black'}>
                {word}
              </Typography>
            ))}
          </Tab>
        ))}
      </Tabs>
      {typeof renderAddNewVehicleButton === 'function' ? (
        renderAddNewVehicleButton()
      ) : (
        <Button
          className="fitment-recent-vehicles__add-vehicle-button"
          isFullWidth
          isPrimary
          onClick={() => {
            onEvent({
              name: EVENTS.VEHICLE_ADD_SELECTED,
              payload: selectedTab
            });
            addVehicleOnClick();
          }}
          size="regular"
          styleName="add-vehicle-button"
        >
          add new vehicle
        </Button>
      )}

      {selectedTab.id === FITMENT_TYPES.SAVED && (
        <div aria-labelledby="tab-saved" id="savedContent" role="tabpanel">
          {isLoggedIn && (
            <>
              {savedFitments?.length > 0 && (
                <Suspense fallback={null}>
                  <FitmentList
                    fitments={savedFitments.slice(0, 3)}
                    hideDelete={hideDelete}
                    hideEditLink={hideEditLink}
                    onEditFitment={handleEditFitment}
                    onFitmentRemoved={fitment =>
                      void onEvent({
                        name: EVENTS.VEHICLE_RECENT_FITMENT_REMOVED,
                        payload: fitment
                      })
                    }
                    onSelectFitment={
                      canToggleVehicle ? onSelectFitment : onFitmentSelected
                    }
                    selectedFitment={selectedFitment}
                  />
                  {savedFitments.length > 3 && (
                    <div
                      className="fitment-recent-vehicles__show-all-container"
                      styleName="show-all-container"
                    >
                      <Button
                        href="#/fitment/my-vehicles"
                        size="regular"
                        styleName="show-all-button"
                      >
                        show all saved vehicles
                      </Button>
                    </div>
                  )}
                </Suspense>
              )}
            </>
          )}
          {typeof renderSavedFitmentsMessage === 'function' &&
            (!isLoggedIn || !savedFitments?.length) && (
              <div
                className="fitment-recent-vehicles__vehicle-selection-text"
                styleName="vehicle-selection-text"
              >
                {renderSavedFitmentsMessage(isLoggedIn)}
              </div>
            )}
        </div>
      )}

      {selectedTab.id === FITMENT_TYPES.RECENT && (
        <div aria-labelledby="tab-recent" id="recentContent" role="tabpanel">
          {recentFitments?.length > 0 && (
            <Suspense fallback={null}>
              <FitmentList
                fitments={recentFitments}
                hideDelete={hideDelete}
                hideEditLink={hideEditLink}
                onEditFitment={handleEditFitment}
                onFitmentRemoved={fitment =>
                  void onEvent({
                    name: EVENTS.VEHICLE_RECENT_FITMENT_REMOVED,
                    payload: fitment
                  })
                }
                onSelectFitment={
                  canToggleVehicle ? onSelectFitment : onFitmentSelected
                }
                selectedFitment={selectedFitment}
              />
            </Suspense>
          )}
          {!recentFitments?.length && (
            <div
              className="fitment-recent-vehicles__vehicle-selection-text"
              styleName="vehicle-selection-text"
            >
              You have no recent vehicle searches
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default FitmentRecentVehicles;
